import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { z } from 'zod';

interface StoredRecord<T> {
    v: number;
    expiresAt: number;
    state: T;
}

const nowMs = () => Date.now();

const safeJsonParse = (raw: string): unknown => {
    try {
        return JSON.parse(raw) as unknown;
    } catch {
        return null;
    }
};

const createKey = () => {
    // Prefer crypto-quality randomness when available.
    if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
        const bytes = new Uint8Array(9);
        crypto.getRandomValues(bytes);
        return Array.from(bytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
    }
    return Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2);
};

export interface UseHybridUrlSessionStateOptions<TState> {
    /** Namespace for storage keys (e.g. 'superAdminDashboard'). */
    namespace: string;
    /** Zod schema used to validate loaded state. */
    schema: z.ZodType<TState>;
    /** Default state when nothing is in storage. */
    defaultState: TState;

    /** URL param name to store the opaque key. Defaults to 'ui'. */
    urlKeyParam?: string;
    /**
     * If true, ensures the URL contains a key on mount (useful when you want to preserve
     * the initial UI state even before any interaction).
     */
    ensureKeyOnMount?: boolean;
    /**
     * If true and ensureKeyOnMount is enabled, writes the initial state to sessionStorage
     * on mount (so a "back" restore has something to rehydrate).
     */
    persistOnMount?: boolean;
    /** How long a stored state should remain valid. Defaults to 2 hours. */
    ttlMs?: number;
    /** Record version. Bump if schema changes in an incompatible way. Defaults to 1. */
    version?: number;
}

export interface HybridUrlSessionStateResult<TState> {
    /** Current validated state. */
    state: TState;
    /** Updates state (and persists it). */
    setState: (updater: TState | ((prev: TState) => TState)) => void;
    /** Clear persisted state and reset to default. */
    reset: () => void;
    /** The current opaque key in the URL (if any). */
    key: string | null;
}

export const defaultTtlMs = 2 * 60 * 60 * 1000; // Set to 2 hours

export const useHybridUrlSessionState = <TState>({
    namespace,
    schema,
    defaultState,
    urlKeyParam = 'ui',
    ensureKeyOnMount = false,
    persistOnMount = false,
    ttlMs = defaultTtlMs,
    version = 1,
}: UseHybridUrlSessionStateOptions<TState>): HybridUrlSessionStateResult<TState> => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlKey = searchParams.get(urlKeyParam);

    const keyRef = useRef<string | null>(urlKey);
    keyRef.current = urlKey;

    const storageKey = useMemo(() => {
        if (!urlKey) {
            return null;
        }
        return `nv.ui.${namespace}.${urlKey}`;
    }, [namespace, urlKey]);

    const readFromSession = useCallback((): TState => {
        if (!storageKey) {
            return defaultState;
        }
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) {
            return defaultState;
        }

        const parsed = safeJsonParse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return defaultState;
        }

        const record = parsed as Partial<StoredRecord<unknown>>;
        if (record.v !== version) {
            return defaultState;
        }
        if (!record.expiresAt || typeof record.expiresAt !== 'number' || nowMs() > record.expiresAt) {
            // Expired: clean up.
            sessionStorage.removeItem(storageKey);
            return defaultState;
        }

        const validated = schema.safeParse(record.state);
        if (!validated.success) {
            return defaultState;
        }

        return validated.data;
    }, [defaultState, schema, storageKey, version]);

    const [state, setStateInternal] = useState<TState>(() => {
        return readFromSession();
    });

    // Keep a ref of the latest state so one-off effects can use the current value
    // without pulling `state` into their dependency arrays.
    const stateRef = useRef(state);
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    const writeToSession = useCallback(
        (key: string, nextState: TState) => {
            const record: StoredRecord<TState> = {
                v: version,
                expiresAt: nowMs() + ttlMs,
                state: nextState,
            };
            sessionStorage.setItem(`nv.ui.${namespace}.${key}`, JSON.stringify(record));
        },
        [namespace, ttlMs, version]
    );

    const ensureUrlKey = useCallback((): string => {
        const existing = keyRef.current;
        if (existing) {
            return existing;
        }

        const key = createKey();
        const next = new URLSearchParams(searchParams);
        next.set(urlKeyParam, key);
        // UI-state changes should not spam history by default.
        setSearchParams(next, { replace: true });
        keyRef.current = key;
        return key;
    }, [searchParams, setSearchParams, urlKeyParam]);

    // Rehydrate when the URL key changes (back/forward navigation).
    useEffect(() => {
        setStateInternal(readFromSession());
    }, [readFromSession]);

    // Optionally create a key immediately so the initial page state can be restored via "from".
    const didInitRef = useRef(false);
    useEffect(() => {
        if (didInitRef.current) {
            return;
        }
        didInitRef.current = true;

        if (!ensureKeyOnMount) {
            return;
        }
        const key = ensureUrlKey();
        if (persistOnMount) {
            writeToSession(key, stateRef.current);
        }
    }, [ensureKeyOnMount, persistOnMount, ensureUrlKey, writeToSession]);

    const setState = useCallback(
        (updater: TState | ((prev: TState) => TState)) => {
            const key = ensureUrlKey();
            setStateInternal(prev => {
                const next = typeof updater === 'function' ? (updater as (p: TState) => TState)(prev) : updater;
                // Validate before persisting to avoid poisoning storage.
                const validated = schema.safeParse(next);
                const finalState = validated.success ? validated.data : defaultState;
                writeToSession(key, finalState);
                return finalState;
            });
        },
        [defaultState, ensureUrlKey, schema, writeToSession]
    );

    const reset = useCallback(() => {
        const key = keyRef.current;
        if (key) {
            sessionStorage.removeItem(`nv.ui.${namespace}.${key}`);
        }
        setStateInternal(defaultState);
    }, [defaultState, namespace]);

    return { state, setState, reset, key: urlKey };
};
